import React from 'react';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';

/** Collapsible amber notes strip under an activity card (Pipedrive-style). */
export function ActivityNotesPanel({
  notes,
  className = 'border-t border-amber-100/90 bg-note-surface px-4 py-2.5',
}: {
  notes?: string | null;
  className?: string;
}) {
  const text = String(notes || '').trim();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [overflows, setOverflows] = React.useState(false);
  const COLLAPSED_MAX_HEIGHT_PX = 72;

  React.useLayoutEffect(() => {
    setExpanded(false);
  }, [text]);

  React.useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || expanded) return;
    setOverflows(el.scrollHeight > COLLAPSED_MAX_HEIGHT_PX + 1);
  }, [text, expanded]);

  if (!text) return null;

  const showToggle = overflows || expanded;

  const toggle = () => setExpanded((v) => !v);

  const onSurfaceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if ((e.target as HTMLElement).closest('button')) return;
    if (window.getSelection()?.toString().trim()) return;
    if (!showToggle) return;
    toggle();
  };

  return (
    <div
      className={`relative activity-notes-panel hover:shadow-[inset_0_0_0_1px_#cbd5e1] ${className} ${
        !expanded && overflows ? 'cursor-pointer' : ''
      }`}
      onClick={onSurfaceClick}
    >
      {showToggle ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className="absolute right-2 top-2 z-10 rounded-md p-1 text-app-text-subtle hover:bg-amber-500/15 hover:text-app-text-muted"
          title={expanded ? 'Notiz einklappen' : 'Notiz vollständig anzeigen'}
          aria-label={expanded ? 'Notiz einklappen' : 'Notiz vollständig anzeigen'}
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronsDownUp className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ChevronsUpDown className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
      ) : null}
      <div className="relative">
        <div
          ref={contentRef}
          className={`text-sm text-app-text-muted whitespace-pre-wrap break-words ${showToggle ? 'pr-7' : ''}`}
          style={expanded ? undefined : { maxHeight: COLLAPSED_MAX_HEIGHT_PX, overflow: 'hidden' }}
        >
          {text}
        </div>
        {!expanded && overflows ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-note-surface to-transparent"
          />
        ) : null}
      </div>
    </div>
  );
}
